/**
 * PlanterView - Three.js plantar projection view with template overlay
 * 
 * Renders foot STL mesh from below (plantar view) with aligned template overlay.
 * Supports manual adjustment: translate (drag), rotate (shift+drag), scale (scroll).
 */

import React, { useRef, useState, useEffect, useMemo, Suspense } from 'react'
import { Canvas, useThree, useLoader } from '@react-three/fiber'
import { OrbitControls, Stats } from '@react-three/drei'
import * as THREE from 'three'
import { STLLoader } from 'three-stdlib'
import { backendService } from '../../../services/backendService'
import { logInfo, logError } from '../../../utils/errorLogger'
import { BoundaryCurve } from './BoundaryCurve'
import { ControlPoints } from './ControlPoints'
import './PlanterView.css'

/**
 * Local error boundary for the 3D scene. STLLoader/useLoader throws errors
 * during the React render phase via Suspense; without a boundary, those
 * errors would unmount the whole CAD Workbench (white screen).
 */
interface SceneErrorBoundaryProps {
  onError: (message: string) => void
  children: React.ReactNode
}
interface SceneErrorBoundaryState {
  hasError: boolean
}
class SceneErrorBoundary extends React.Component<SceneErrorBoundaryProps, SceneErrorBoundaryState> {
  state: SceneErrorBoundaryState = { hasError: false }
  static getDerivedStateFromError(): SceneErrorBoundaryState {
    return { hasError: true }
  }
  componentDidCatch(error: Error) {
    const message = error?.message || 'Unknown 3D scene error'
    logError(`PlanterView scene error: ${message}`, 'PlanterView')
    this.props.onError(message)
  }
  render() {
    if (this.state.hasError) return null
    return this.props.children
  }
}

interface PlanterViewProps {
  projectId: string
  stlFileId: string
  alignedTemplate: number[][]
  plantarProjection?: number[][] | null
  boundaryCurve3d?: number[][] | null
  onManualAdjust: (translation: [number, number], rotation: number, scale: number) => void
  /** Control points for editing mode */
  controlPoints?: number[][] | null
  /** Callback when control points change */
  onControlPointsChange?: (newPoints: number[][]) => void
  /** Whether in editing mode */
  isEditingBoundary?: boolean
  /** Whether smooth region selection mode is active */
  isSmoothMode?: boolean
  /** Callback when a control point is clicked in smooth mode */
  onSmoothRegionSelect?: (index: number) => void
  /** Smooth region start index */
  smoothStartIndex?: number
  /** Smooth region end index */
  smoothEndIndex?: number
}

/**
 * Template overlay component - renders template fill and outline
 */
interface TemplateOverlayProps {
  points: number[][]
  translation: [number, number]
  rotation: number
  scale: number
  dimmed?: boolean
}

const TemplateOverlay: React.FC<TemplateOverlayProps> = ({ points, translation, rotation, scale, dimmed = false }) => {
  const shape = useMemo(() => {
    if (points.length < 3) return null

    const shape = new THREE.Shape()
    shape.moveTo(points[0][0], points[0][1])
    for (let i = 1; i < points.length; i++) {
      shape.lineTo(points[i][0], points[i][1])
    }
    shape.closePath()
    return shape
  }, [points])

  const linePoints = useMemo(() => {
    const pts = points.map(([x, y]) => new THREE.Vector3(x, y, 0.1))
    pts.push(pts[0]) // Close the loop
    return pts
  }, [points])

  if (!shape) return null

  const fillOpacity = dimmed ? 0.15 : 0.4
  const lineOpacity = dimmed ? 0.3 : 0.8

  return (
    <group position={[translation[0], translation[1], 0]} rotation={[0, 0, rotation]} scale={[scale, scale, 1]}>
      {/* Template fill */}
      <mesh position={[0, 0, 0.05]}>
        <shapeGeometry args={[shape]} />
        <meshBasicMaterial color="#66BB6A" transparent opacity={fillOpacity} side={THREE.DoubleSide} />
      </mesh>

      {/* Template outline */}
      <line>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={linePoints.length}
            array={new Float32Array(linePoints.flatMap(p => [p.x, p.y, p.z]))}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color="#66BB6A" linewidth={2} opacity={lineOpacity} transparent />
      </line>
    </group>
  )
}

/**
 * STL Mesh component
 */
interface STLMeshProps {
  url: string
  opacity?: number
  meshRef?: React.MutableRefObject<THREE.Mesh | null>
}

const STLMesh: React.FC<STLMeshProps> = ({ url, opacity = 1.0, meshRef }) => {
  const geometry = useLoader(STLLoader, url)
  const localMeshRef = useRef<THREE.Mesh>(null)

  // Center and scale the geometry
  useEffect(() => {
    if (geometry) {
      geometry.computeBoundingBox()
      const bbox = geometry.boundingBox
      if (bbox) {
        const center = new THREE.Vector3()
        bbox.getCenter(center)
        geometry.translate(-center.x, -center.y, -center.z)
      }
    }
  }, [geometry])

  // Expose mesh ref
  useEffect(() => {
    if (meshRef && localMeshRef.current) {
      meshRef.current = localMeshRef.current
    }
  }, [meshRef])

  return (
    <mesh ref={localMeshRef} geometry={geometry}>
      <meshStandardMaterial
        color="#AAAAAA"
        flatShading
        transparent={opacity < 1.0}
        opacity={opacity}
      />
    </mesh>
  )
}

/**
 * Scene content component with controls
 */
interface SceneContentProps {
  stlUrl: string
  alignedTemplate: number[][]
  boundaryCurve3d?: number[][] | null
  translation: [number, number]
  rotation: number
  scale: number
  onAdjust: (translation: [number, number], rotation: number, scale: number) => void
  controlPoints?: number[][] | null
  onControlPointsChange?: (newPoints: number[][]) => void
  isEditingBoundary?: boolean
  footMeshRef?: React.MutableRefObject<THREE.Mesh | null>
  isSmoothMode?: boolean
  onSmoothRegionSelect?: (index: number) => void
  smoothStartIndex?: number
  smoothEndIndex?: number
}

const SceneContent: React.FC<SceneContentProps> = ({
  stlUrl,
  alignedTemplate,
  boundaryCurve3d,
  translation,
  rotation,
  scale,
  controlPoints,
  onControlPointsChange,
  isEditingBoundary,
  footMeshRef,
  isSmoothMode,
  onSmoothRegionSelect,
  smoothStartIndex,
  smoothEndIndex
}) => {
  const { camera } = useThree()
  const hasBoundary = !!(boundaryCurve3d && boundaryCurve3d.length > 0)

  useEffect(() => {
    if (hasBoundary) {
      // 3D view: position camera to see the boundary from an angle
      camera.position.set(30, 30, 30)
      camera.lookAt(0, 0, 0)
      camera.up.set(0, 0, 1)
    } else {
      // Plantar view: look down from below
      camera.position.set(0, 0, -50)
      camera.lookAt(0, 0, 0)
      camera.up.set(0, 1, 0)
    }
  }, [camera, hasBoundary])

  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[0, 0, -10]} intensity={0.8} />
      <directionalLight position={[0, 0, 10]} intensity={0.4} />

      <STLMesh url={stlUrl} opacity={hasBoundary ? 0.8 : 1.0} meshRef={footMeshRef} />
      
      <TemplateOverlay
        points={alignedTemplate}
        translation={translation}
        rotation={rotation}
        scale={scale}
        dimmed={hasBoundary}
      />

      {hasBoundary && !isEditingBoundary && (
        <BoundaryCurve
          boundaryCurve3d={boundaryCurve3d}
          color="#66BB6A"
          linewidth={3}
          opacity={1.0}
        />
      )}

      {isEditingBoundary && controlPoints && controlPoints.length > 0 && (
        <>
          <BoundaryCurve
            boundaryCurve3d={controlPoints}
            color="#66BB6A"
            linewidth={3}
            opacity={0.6}
          />
          <ControlPoints
            points={controlPoints}
            onPointsChange={onControlPointsChange || (() => {})}
            footMesh={footMeshRef?.current || null}
            pointRadius={1.0}
            normalColor="#FF9800"
            hoverColor="#00CED1"
            isSmoothMode={isSmoothMode}
            onSmoothRegionSelect={onSmoothRegionSelect}
            smoothStartIndex={smoothStartIndex}
            smoothEndIndex={smoothEndIndex}
          />
        </>
      )}

      <OrbitControls
        enableRotate={hasBoundary}
        enablePan={true}
        enableZoom={true}
      />

      <gridHelper args={[100, 20, '#444444', '#222222']} rotation={hasBoundary ? [0, 0, 0] : [Math.PI / 2, 0, 0]} />
    </>
  )
}

/**
 * Main PlanterView component
 */
export const PlanterView: React.FC<PlanterViewProps> = ({
  projectId,
  stlFileId,
  alignedTemplate,
  plantarProjection,
  boundaryCurve3d,
  onManualAdjust,
  controlPoints,
  onControlPointsChange,
  isEditingBoundary = false,
  isSmoothMode = false,
  onSmoothRegionSelect,
  smoothStartIndex = -1,
  smoothEndIndex = -1
}) => {
  const [stlUrl, setStlUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  
  const [translation, setTranslation] = useState<[number, number]>([0, 0])
  const [rotation, setRotation] = useState<number>(0)
  const [scale, setScale] = useState<number>(1.0)
  
  const isDragging = useRef(false)
  const isShiftPressed = useRef(false)
  const lastMousePos = useRef<[number, number]>([0, 0])
  const footMeshRef = useRef<THREE.Mesh | null>(null)

  // The alignment phase runs in 2D (plantar projection + 2D template). The
  // 3D STL path is only needed once we have a draped 3D boundary curve to
  // edit, so we skip the STL download until then. Mixing the 3D STL mesh
  // (in raw STL coordinates) with the 2D template (in plantar-projection
  // coordinates) is what produced the historical "template floats off in
  // empty space" bug.
  const hasBoundary = !!(boundaryCurve3d && boundaryCurve3d.length > 0)
  const use2DView = !hasBoundary

  useEffect(() => {
    if (use2DView) {
      // 2D alignment phase: no STL needed.
      setLoading(false)
      setError(null)
      setStlUrl(null)
      return
    }

    let cancelled = false
    let createdBlobUrl: string | null = null

    const loadSTL = async () => {
      try {
        setLoading(true)
        setError(null)

        // Fetch the STL bytes via the authenticated backend service. The
        // STLLoader used inside the Canvas does a plain fetch that cannot
        // attach the Authorization header, so we fetch the binary here and
        // hand it off as a Blob URL.
        const buffer = await backendService.downloadStlAsArrayBuffer(projectId, 'stl_in')
        if (cancelled) return

        const blob = new Blob([buffer], { type: 'model/stl' })
        const blobUrl = URL.createObjectURL(blob)
        createdBlobUrl = blobUrl

        setStlUrl(blobUrl)
        setLoading(false)
        logInfo(`STL loaded for plantar view (project=${projectId}, file=${stlFileId}, ${buffer.byteLength} bytes)`, 'PlanterView')
      } catch (err) {
        if (cancelled) return
        const message = err instanceof Error ? err.message : 'Failed to load STL'
        logError(`STL loading error: ${message}`, 'PlanterView')
        setError(message)
        setLoading(false)
      }
    }

    loadSTL()

    return () => {
      cancelled = true
      if (createdBlobUrl) {
        URL.revokeObjectURL(createdBlobUrl)
      }
    }
  }, [projectId, stlFileId, use2DView])

  const handleAdjust = (newTranslation: [number, number], newRotation: number, newScale: number) => {
    setTranslation(newTranslation)
    setRotation(newRotation)
    setScale(newScale)
    onManualAdjust(newTranslation, newRotation, newScale)
  }
  
  const handlePointerDown = (e: React.PointerEvent) => {
    isDragging.current = true
    isShiftPressed.current = e.shiftKey
    lastMousePos.current = [e.clientX, e.clientY]
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current) return

    const dx = e.clientX - lastMousePos.current[0]
    const dy = e.clientY - lastMousePos.current[1]
    lastMousePos.current = [e.clientX, e.clientY]

    if (isShiftPressed.current) {
      // Rotation: horizontal drag rotates
      const newRotation = rotation + dx * 0.01
      handleAdjust(translation, newRotation, scale)
    } else {
      // Translation: drag to move
      const newTranslation: [number, number] = [
        translation[0] + dx * 0.1,
        translation[1] - dy * 0.1
      ]
      handleAdjust(newTranslation, rotation, scale)
    }
  }

  const handlePointerUp = () => {
    isDragging.current = false
  }

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 0.95 : 1.05
    const newScale = Math.max(0.5, Math.min(2.0, scale * delta))
    handleAdjust(translation, rotation, newScale)
  }

  // ─── 2D alignment view ───────────────────────────────────────────────
  // Used during the alignment phase (before 3D draping). Renders the
  // plantar projection (the actual outline of the bottom of the foot)
  // and the aligned template in the SAME 2D coordinate frame, so the
  // template is placed correctly relative to the foot. This is the view
  // the doctor uses to confirm the calibration-driven ICP result before
  // moving on to drape.
  if (use2DView) {
    const projection = plantarProjection || []
    const template = alignedTemplate || []

    // Compute viewBox over both shapes so the user always sees them.
    const allPts: number[][] = []
    if (projection.length) allPts.push(...projection)
    if (template.length) allPts.push(...template)

    let viewBox = '-150 -200 300 400'
    let worldHeight = 400
    if (allPts.length > 0) {
      const xs = allPts.map(p => p[0])
      const ys = allPts.map(p => p[1])
      const margin = 20
      const minX = Math.min(...xs) - margin
      const minY = Math.min(...ys) - margin
      const w = Math.max(...xs) - Math.min(...xs) + 2 * margin
      const h = Math.max(...ys) - Math.min(...ys) + 2 * margin
      viewBox = `${minX} ${minY} ${w} ${h}`
      worldHeight = h
    }

    // Apply translate / rotate / scale to template points (mirrors the
    // backend's manual adjust transform: affine in the same 2D frame).
    const cosR = Math.cos(rotation)
    const sinR = Math.sin(rotation)
    const transformedTemplate = template.map(([x, y]) => {
      const sx = x * scale
      const sy = y * scale
      const rx = sx * cosR - sy * sinR
      const ry = sx * sinR + sy * cosR
      return [rx + translation[0], ry + translation[1]] as [number, number]
    })

    return (
      <div
        className="planter-view"
        onPointerDown={handlePointerDown}
        onPointerMove={(e) => {
          if (!isDragging.current) return
          const dx = e.clientX - lastMousePos.current[0]
          const dy = e.clientY - lastMousePos.current[1]
          lastMousePos.current = [e.clientX, e.clientY]
          // Convert pixel delta to world-mm using viewBox height / canvas height.
          const target = e.currentTarget as HTMLElement
          const pxPerMm = target.clientHeight > 0 ? target.clientHeight / worldHeight : 1
          const mmPerPx = pxPerMm > 0 ? 1 / pxPerMm : 0.1
          if (isShiftPressed.current) {
            handleAdjust(translation, rotation + dx * 0.01, scale)
          } else {
            handleAdjust(
              [translation[0] + dx * mmPerPx, translation[1] - dy * mmPerPx],
              rotation,
              scale
            )
          }
        }}
        onPointerUp={handlePointerUp}
        onWheel={handleWheel}
      >
        <div className="view-instructions">
          <p>
            <strong>Drag</strong> to translate | <strong>Shift+Drag</strong> to rotate | <strong>Scroll</strong> to scale
            {projection.length === 0 && ' | ⚠️ no plantar projection from backend'}
          </p>
        </div>
        <svg
          viewBox={viewBox}
          style={{ width: '100%', height: '100%', background: '#1a1a1a', display: 'block' }}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Foot plantar projection — the bottom of the foot */}
          {projection.length > 2 && (
            <polygon
              points={projection.map(p => `${p[0]},${p[1]}`).join(' ')}
              fill="rgba(180, 180, 180, 0.35)"
              stroke="#888"
              strokeWidth={Math.max(0.4, worldHeight / 400)}
            />
          )}
          {/* Aligned template */}
          {transformedTemplate.length > 2 && (
            <polygon
              points={transformedTemplate.map(p => `${p[0]},${p[1]}`).join(' ')}
              fill="rgba(102, 187, 106, 0.35)"
              stroke="#66BB6A"
              strokeWidth={Math.max(0.6, worldHeight / 300)}
            />
          )}
        </svg>
      </div>
    )
  }
  // ─────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="planter-view-loading">
        <div className="spinner"></div>
        <p>Loading STL mesh...</p>
      </div>
    )
  }

  if (error || !stlUrl) {
    return (
      <div className="planter-view-error">
        <span className="error-icon">⚠️</span>
        <p>{error || 'Failed to load STL'}</p>
      </div>
    )
  }

  return (
    <div
      className="planter-view"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onWheel={handleWheel}
    >
      <div className="view-instructions">
        <p>
          {isEditingBoundary ? (
            <>
              <strong>Click and drag</strong> control points to adjust boundary | <strong>Orange spheres</strong> are control points
            </>
          ) : (
            <>
              <strong>Drag</strong> to translate | <strong>Shift+Drag</strong> to rotate | <strong>Scroll</strong> to scale
              {boundaryCurve3d && boundaryCurve3d.length > 0 && ' | 3D boundary curve active'}
            </>
          )}
        </p>
      </div>
      <Canvas
        camera={{ position: [0, 0, -50], fov: 50 }}
        style={{ width: '100%', height: '100%', background: '#1a1a1a' }}
      >
        <SceneErrorBoundary onError={(msg) => setError(msg)}>
          <Suspense fallback={null}>
            <SceneContent
              stlUrl={stlUrl}
              alignedTemplate={alignedTemplate}
              boundaryCurve3d={boundaryCurve3d}
              translation={translation}
              rotation={rotation}
              scale={scale}
              onAdjust={handleAdjust}
              controlPoints={controlPoints}
              onControlPointsChange={onControlPointsChange}
              isEditingBoundary={isEditingBoundary}
              footMeshRef={footMeshRef}
              isSmoothMode={isSmoothMode}
              onSmoothRegionSelect={onSmoothRegionSelect}
              smoothStartIndex={smoothStartIndex}
              smoothEndIndex={smoothEndIndex}
            />
          </Suspense>
        </SceneErrorBoundary>
        <Stats />
      </Canvas>
    </div>
  )
}
